/**
 * useCustomsManifest - Portless customs-manifest station hooks
 *
 * Wraps the `customsManifest` tRPC router for the packing station's
 * Manifests section. Three calls:
 *  - getStatus:  is this a customs location? PER-CLIENT unmanifested counts +
 *                recent manifests. skipToken when no location.
 *  - generate:   mint + return the next manifest for ONE client org (base64
 *                XLSX) and invalidate the status query so the counts/recent
 *                lists refresh.
 *  - download:   re-fetch a previously generated manifest's base64 on demand.
 *
 * CROSS-ORG: at a shared 3PL location the caller may be able to declare for
 * several client orgs (Thread Lab operating Threadheads + Mini Zali parcels).
 * A manifest is always exactly ONE client's shipments, so `generate` takes the
 * client org id explicitly; the backend re-authorises it against the same
 * per-client list that produced `getStatus.clients`.
 *
 * FOLLOWS: usePackingStats.ts (skipToken query) + useShipping.ts (useUtils
 * invalidation) patterns.
 */
import { useCallback } from 'react';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { skipToken } from '@tanstack/react-query';

/** Full customs status payload for a location (per-client breakdown). */
export type CustomsManifestStatus = RouterOutputs['customsManifest']['getStatus'];
/** One client org's slice: org identity, ready-count and its own recent list. */
export type CustomsManifestClient = CustomsManifestStatus['clients'][number];
/** A single row of a client's recent-manifest list. */
export type RecentCustomsManifest = CustomsManifestClient['recent'][number];

/**
 * Query for the customs-manifest status at a location.
 *
 * `applicable` is false for any location that declares customs for no org the
 * caller may act for — the consuming section renders nothing in that case.
 *
 * @param locationId - The location ID to check, or null (query skipped)
 */
export function useCustomsManifestStatus(locationId: string | null) {
  return trpc.customsManifest.getStatus.useQuery(
    locationId ? { locationId } : skipToken,
    {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    }
  );
}

/**
 * Mutation to generate the next customs manifest for ONE client org at a
 * location (`{ locationId, clientOrganizationId }`). Returns the base64 XLSX
 * for immediate download; invalidates getStatus so every client's unmanifested
 * count + recent list refresh.
 */
export function useGenerateCustomsManifest() {
  const utils = trpc.useUtils();

  return trpc.customsManifest.generate.useMutation({
    onSuccess: () => {
      utils.customsManifest.getStatus.invalidate();
    },
  });
}

/**
 * Imperative re-download of a previously generated manifest.
 * READ-ONLY — no cache invalidation. Returns a stable fetcher keyed off
 * trpc utils so it can live in a callback without re-creating each render.
 */
export function useDownloadCustomsManifest() {
  const utils = trpc.useUtils();

  return useCallback(
    (manifestId: string) => utils.customsManifest.download.fetch({ manifestId }),
    [utils]
  );
}
